import { useVhcRecordModal } from '../../../useVhcRecordModal';
import { useWaitingStore } from '../waiting-context';
import { PartsSection } from './parts';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { VhcItem } from 'features/vhc/model';
import { useVhcStore } from 'features/vhc/model/vhc-store';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

export const LaboursPartsSection = () => {
  const { isWaiting } = useWaitingStore();

  const {
    control,
    trigger,
    formState: { isValid, errors }
  } = useFormContext<VhcItem>();

  const isMultipleOf005 = (value: number): boolean => {
    const normalized = Math.round((value + Number.EPSILON) * 100);
    return normalized % 5 === 0;
  };

  const {
    fields: labours,
    append: appendLabours,
    remove: removeLabors
  } = useFieldArray({
    control,
    name: `labours`
  });

  const { recordModalData } = useVhcStore();
  const { isReport, isComplete } = recordModalData || {};

  return (
    <div className="mb-4 ps-2">
      <h5 className="fw-bold fs-3 text-gray-900 row">Required Labours</h5>
      <div className="mt-5 fw-medium">
        {labours.length > 0 && (
          <div className="row align-items-center mb-2 d-flex gap-6">
            <span className="col-8 col-lg-8 px-0 d-none d-lg-block">Labours</span>
            <span className="col-3 col-lg-2 px-0 d-none d-lg-block">Time (in hours)</span>
          </div>
        )}
        {labours?.map((field, index) => (
          <div
            key={field.id + index.toString()}
            className={clsx(styles.mobileDashedBorderBlue, styles.labourPartRow, 'row d-flex gap-4')}
          >
            <div className="col-12 col-lg-8 mb-3 mb-lg-0 px-0">
              <span className="d-lg-none d-block">Labours</span>
              <Controller
                name={`labours.${index}.title`}
                control={control}
                rules={{
                  required: 'Labour description is required'
                }}
                render={({ field, fieldState: { error } }) => (
                  <div className="position-relative">
                    <Form.Control
                      {...field}
                      className={`col form-control-solid py-2 fw-bold w-100 ${error ? 'border-danger' : ''}`}
                      placeholder="Labour description"
                      disabled={isComplete}
                      isInvalid={!!error}
                      onBlur={() => {
                        trigger(`labours.${index}.title`);
                      }}
                    />
                    {error && <div className={styles.errorMessage}>{error.message}</div>}
                  </div>
                )}
              />
            </div>
            <div className="col-12 col-lg-2 mb-3 mb-lg-0 d-lg-flex flex-lg-grow-1 px-0 ">
              <span className="d-lg-none d-block">Time (in hours)</span>
              <div className="d-flex h-35px gap-2 justify-content-between">
                <Controller
                  name={`labours.${index}.time`}
                  control={control}
                  rules={{
                    validate: (timeInMinutes) => {
                      const hours = timeInMinutes / 60;
                      if (isNaN(hours) || !isFinite(hours)) return false;
                      return (
                        isMultipleOf005(hours) ||
                        'Time must be a multiple of 3 minutes' /* (example: 6 minutes - 0.1, 15 minutes - 0.25, 30 minutes - 0.5, 60 minutes - 1) */
                      );
                    }
                  }}
                  render={({ field, fieldState: { error } }) => {
                    const [inputValue, setInputValue] = useState(field.value ? (field.value / 60).toFixed(2) : '0.00');
                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value.replace(',', '.');
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        if (/^\d$/.test(value) && value.length > inputValue.length) {
                          value = `${value}.`;
                        }
                        setInputValue(value);
                        const hours = value === '' ? 0 : parseFloat(value);
                        field.onChange(hours * 60);
                      }
                    };
                    const handleBlur = () => {
                      const hours = parseFloat(inputValue) || 0;
                      const formattedValue = hours.toFixed(2);
                      setInputValue(formattedValue);
                      field.onChange((hours * 60).toFixed());
                      trigger(`labours.${index}.time`);
                    };

                    return (
                      <div className="position-relative">
                        <Form.Control
                          value={inputValue}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`col form-control-solid py-2 fw-bold w-100 ${error ? 'border-danger' : ''}`}
                          isInvalid={!!error}
                          placeholder="0.00"
                          style={{ minWidth: 80 }}
                        />
                        {error && <div className={styles.errorMessage}>{error.message}</div>}
                      </div>
                    );
                  }}
                />
                <span className="d-flex align-items-center text-end">
                  {!isReport && !isComplete && (
                    <button
                      type="button"
                      className="p-0 border-0 bg-transparent"
                      onClick={() => {
                        removeLabors(index);
                        trigger();
                      }}
                    >
                      <InlineSvg
                        path="/media/icons/duotune/general/gen040.svg"
                        svgClassName="w-30px h-30px text-secondary"
                      />
                    </button>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
        {!isReport && !isComplete && (
          <div className="row">
            <div className="col-2 px-0">
              <button
                disabled={(!isValid && !!errors?.labours?.length) || isWaiting}
                type="button"
                className="btn btn-secondary px-4 py-2 d-flex align-items-center"
                onClick={() => {
                  appendLabours({
                    time: 0,
                    title: ''
                  });
                }}
              >
                <InlineSvg
                  path="/media/icons/custom/plus-circle.svg"
                  className="svg-icon-2"
                  svgClassName="w-20px h-20px"
                />
                <span className="fw-medium text-white fs-6">Add</span>
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="row" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '1rem' }} />
      <h5 className="fw-bold fs-3 text-gray-900 row mt-8">Required Parts</h5>
      <div className="mb-3 mt-4 fw-medium">
        <PartsSection />
      </div>
    </div>
  );
};
